import React, { useState } from "react";

/* Floating Input */
const FloatingInput = ({ label, type = "text", name, required, onChange }) => (
  <div className="relative">
    <input
      type={type}
      name={name}
      required={required}
      onChange={onChange}
      placeholder=" "
      className="peer w-full bg-slate-800 border border-slate-700 rounded-lg px-4 pt-5 pb-2 text-stone-200 focus:outline-none focus:border-violet-500"
    />
    <label className="absolute left-4 top-2 text-sm text-slate-400 transition-all 
      peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base 
      peer-placeholder-shown:text-slate-500 peer-focus:top-2 peer-focus:text-sm peer-focus:text-violet-400">
      {label}
    </label>
  </div>
);

/* Select */
const FloatingSelect = ({ label, name, options, required, onChange }) => (
  <div className="relative">
    <select
      name={name}
      required={required}
      onChange={onChange}
      defaultValue=""
      className="peer w-full bg-slate-800 border border-slate-700 rounded-lg px-4 pt-5 pb-2 text-stone-200 focus:outline-none focus:border-violet-500"
    >
      <option value="" disabled hidden></option>
      {options.map(opt => <option key={opt}>{opt}</option>)}
    </select>
    <label className="absolute left-4 top-2 text-sm text-slate-400 transition-all peer-focus:text-violet-400">
      {label}
    </label>
  </div>
);

const DynamicForm = ({ fields = [], onSubmit, submitLabel }) => {
  const [formData, setFormData] = useState({});
  const [error, setError] = useState("");

  const handleChange = (e, field) => {
    const { type, files, value, checked } = e.target;

    if (type === "file") {
      const file = files[0];
      if (file && file.size > 5 * 1024 * 1024) {
        setError("Resume must be under 5MB");
        return;
      }
      setError("");
      setFormData(prev => ({ ...prev, [field.name]: file }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field.name]: type === "checkbox" ? checked : value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (error) return;
    onSubmit(formData);
  };

  const renderField = (field) => {
    switch (field.type) {
      case "text":
      case "email":
      case "date":
        return (
          <FloatingInput
            label={field.label}
            type={field.type}
            name={field.name}
            required={field.required}
            onChange={(e) => handleChange(e, field)}
          />
        );

      case "select":
        return (
          <FloatingSelect
            label={field.label}
            name={field.name}
            options={field.options}
            required={field.required}
            onChange={(e) => handleChange(e, field)}
          />
        );

      case "file":
        return (
          <div>
            <label className="block text-sm text-slate-400 mb-2">{field.label}</label>
            <input
              type="file"
              accept={field.accept}
              onChange={(e) => handleChange(e, field)}
              className="text-sm text-stone-300 file:bg-violet-600 file:text-white file:border-none file:px-4 file:py-2 file:rounded-lg"
            />
          </div>
        );

      case "checkbox":
        return (
          <label className="flex gap-2 text-sm text-slate-400">
            <input type="checkbox" required={field.required} onChange={(e) => handleChange(e, field)} />
            {field.label}
          </label>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-10">
      {error && <div className="text-red-400 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {fields.map((field, idx) => (
          <div key={idx} className={field.fullWidth ? "md:col-span-2" : ""}>
            {renderField(field)}
          </div>
        ))}
      </div>

      <button
        type="submit"
        className="w-full bg-gradient-to-r from-violet-600 to-amber-600 hover:opacity-90 text-white font-semibold py-3 rounded-lg shadow-lg transition-all"
      >
        {submitLabel}
      </button>
    </form>
  );
};

export default DynamicForm;
